/**
 * Hook to register Connections tab with DetailPageTabsContext
 * 
 * This hook automatically adds the Connections tab to any doctype detail page.
 * It's meta-driven - works for all doctypes without additional configuration.
 * 
 * Best Practices:
 * - Uses functional updates for setTabs (prev => ...) to avoid dependency issues
 * - No dependency on tabs array (prevents loops/glitches)
 * - Automatically manages tab lifecycle (add/remove based on conditions)
 * - Uses constant for tab ID to ensure consistency
 */

import { useEffect } from 'react';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';

/**
 * Tab ID constant - use this everywhere you reference the Connections tab
 */
export const CONNECTIONS_TAB_ID = 'connections';

interface UseConnectionsTabOptions {
  doctype: string;
  name?: string;
  enabled?: boolean; // Only show for existing documents
}

/**
 * Hook to register Connections tab with DetailPageTabsContext.
 * 
 * This makes the Connections tab available in the tab navigation
 * for any doctype detail page.
 * 
 * The tab is automatically:
 * - Added when document has a name (not new)
 * - Removed when document is new
 * - Positioned at the end of the tab list
 * 
 * Uses functional updates to prevent dependency issues and tab duplication.
 */
export function useConnectionsTab({ 
  doctype, 
  name, 
  enabled = true 
}: UseConnectionsTabOptions) {
  const { setTabs } = useDetailPageTabs();
  
  useEffect(() => {
    const shouldShow = enabled && !!doctype && !!name && name !== 'new';
    
    setTabs((prevTabs: TabStructure[]) => {
      const exists = prevTabs.some(t => t.name === CONNECTIONS_TAB_ID);
      
      // Add tab if conditions met and doesn't exist
      if (shouldShow && !exists) {
        // Create a TabStructure for Connections tab
        // It has empty sections so TabRenderer won't try to render it
        const connectionsTab: TabStructure = {
          name: CONNECTIONS_TAB_ID,
          label: 'Connections',
          sections: [], // Empty sections - this tab is handled separately in SystemView
        };
        
        return [...prevTabs, connectionsTab];
      }
      
      // Remove tab if conditions not met and exists
      if (!shouldShow && exists) {
        return prevTabs.filter(t => t.name !== CONNECTIONS_TAB_ID);
      }
      
      // No change needed
      return prevTabs;
    });
  }, [doctype, name, enabled, setTabs]);
  
  return {
    tabId: CONNECTIONS_TAB_ID,
    isEnabled: enabled && !!name && name !== 'new' && !!doctype,
  };
}
